import type { ReactElement } from 'react'

import { useAuth } from '../lib/auth'
import { useT } from '../lib/i18n'
import { useDateRange } from '../lib/range'
import { logoFor, useBranding } from '../lib/branding'
import { modifierLabel } from '../hooks/useHotkey'
import { useIsMobile } from '../hooks/useMediaQuery'
import { ButtonLink } from '../components/ui/Button'
import { DateRangePicker } from '../components/ui/DateRangePicker'
import { Icon } from '../components/ui/Icon'
import { IconButton } from '../components/ui/IconButton'
import { LocaleSwitcher } from '../components/ui/LocaleSwitcher'
import { NotificationBell } from '../features/notifications/NotificationBell'
import { HelpMenu } from '../features/tours/HelpMenu'
import { BrandSwitcher } from './BrandSwitcher'
import { UserMenu } from './UserMenu'

export interface TopBarProps {
  onOpenCommandPalette: () => void
}

/** Chrome-900, 56 px: brand context on the left, range and search in the middle, account right. */
export const TopBar = ({ onOpenCommandPalette }: TopBarProps): ReactElement => {
  const t = useT()
  const { range, setRange } = useDateRange()
  const { activeBrand } = useAuth()
  const { branding } = useBranding()
  const isMobile = useIsMobile()
  const logo = logoFor(branding, 'chrome')

  return (
    <header className="flex h-14 shrink-0 items-center gap-2 border-b border-chrome-600 bg-chrome-900 px-3 md:px-4">
      {isMobile ? (
        <span className="flex h-8 shrink-0 items-center pr-1">
          {logo ? (
            <img src={logo} alt={branding.productName} className="max-h-6 max-w-24 object-contain" />
          ) : (
            <span className="t-label text-brass">{branding.productName}</span>
          )}
        </span>
      ) : null}

      <BrandSwitcher />

      {!isMobile && activeBrand ? (
        <ButtonLink
          to="/app/planner?compose=1"
          variant="primary"
          size="sm"
          iconLeft={<Icon name="plus" size={14} />}
        >
          {t('planner.toolbar.compose')}
        </ButtonLink>
      ) : null}

      <div className="ml-auto flex items-center gap-2">
        {!isMobile ? (
          <>
            <DateRangePicker value={range} onChange={setRange} tone="chrome" />
            <button
              type="button"
              onClick={onOpenCommandPalette}
              className="ring-focus flex h-9 items-center gap-2 rounded-sm border border-chrome-600 bg-chrome-800 px-3 text-chrome-mute transition-colors hover:bg-chrome-700 hover:text-chrome-ink"
            >
              <Icon name="search" size={15} />
              <span className="t-small">{t('common.actions.search')}</span>
              <kbd className="t-mono rounded-[3px] border border-chrome-600 px-1">
                {modifierLabel()} K
              </kbd>
            </button>
            <LocaleSwitcher variant="dropdown" surface="chrome" />
          </>
        ) : (
          <IconButton
            label={t('shell.commandPalette.search')}
            icon={<Icon name="search" size={18} />}
            tone="chrome"
            onClick={onOpenCommandPalette}
            tooltip={false}
          />
        )}
        {/* Beside the account, on both widths: the bell is the one chrome control that is worth
            reaching on a phone, because it is the only one that can be *new* since you last
            looked. */}
        <HelpMenu />
        <NotificationBell />
        <UserMenu />
      </div>

      {activeBrand ? (
        <span className="sr-only" aria-live="polite">
          {t('shell.topBar.activeBrand', { name: activeBrand.name })}
        </span>
      ) : null}
    </header>
  )
}

/** Compact range control for mobile, rendered by AppShell under the top bar. */
export const MobileRangeBar = (): ReactElement => {
  const { range, setRange } = useDateRange()
  return (
    <div className="flex items-center justify-end border-b border-hair bg-surface px-4 py-2 md:hidden">
      <DateRangePicker value={range} onChange={setRange} />
    </div>
  )
}
